import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Waves, MoveRight, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { listActivitiesApi } from "@/lib/api";
import { OceanBackground } from "@/components/AppShell";
import { pickMessage } from "@/lib/messages";
import { activityIcon } from "@/lib/constants";
import { useChallengeCelebration } from "@/hooks/useChallengeCelebration";
import { ChallengeCelebration } from "@/components/community/ChallengeCelebration";

export default function MotionCardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useProfile();
  const { t, minutesLong } = useLanguage();
  const [activity, setActivity] = useState(location.state?.activity || null);
  const [checked, setChecked] = useState(!!location.state?.activity);
  // Stage 2.2: the moment an activity is saved is when a challenge can complete
  const { celebration, dismiss } = useChallengeCelebration();

  useEffect(() => {
    // Fallback: opened directly — show the latest activity
    if (!activity) {
      listActivitiesApi(profile.id, 1)
        .then((list) => {
          if (list.length > 0) setActivity(list[0]);
          setChecked(true);
        })
        .catch(() => setChecked(true));
    }
  }, [activity, profile.id]);

  useEffect(() => {
    if (checked && !activity) navigate("/", { replace: true });
  }, [checked, activity, navigate]);

  if (!activity) return null;

  const Icon = activityIcon(activity.type);
  const salt = activity.id ? activity.id.charCodeAt(0) + activity.duration_minutes : 0;
  const status = pickMessage(t("motionCard.statusPool"), salt);
  const sentence = pickMessage(t("motionCard.sentencePool"), salt + 1);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <OceanBackground variant="sheen" />

      <div
        data-testid="motion-card-screen"
        className="relative z-10 max-w-md mx-auto px-5 min-h-screen flex flex-col justify-center py-10"
      >
        {/* Ripple rings behind the card */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <div className="ripple-ring absolute h-72 w-72 rounded-full border border-cyan-300/20" />
          <div className="ripple-ring-delay absolute h-72 w-72 rounded-full border border-cyan-300/15" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl bg-white/[0.06] border border-white/[0.12] backdrop-blur-xl p-6 shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
        >
          <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(500px_circle_at_30%_0%,rgba(34,211,238,0.10),transparent_60%)]" />

          <div className="relative">
            {/* Brand header — never translated */}
            <div className="flex items-center gap-2">
              <Waves size={16} className="text-cyan-300" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-cyan-300/90 font-semibold">
                Blue Ocean Motion
              </span>
            </div>

            {/* Activity icon */}
            <div className="mt-6 flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-cyan-300/15 border border-cyan-300/30 flex items-center justify-center">
                <Icon size={26} className="text-cyan-300" />
              </div>
              <div>
                <p
                  data-testid="motion-card-user"
                  className="text-sm text-[hsl(var(--muted-foreground))]"
                >
                  {profile.name || t("motionCard.you")}
                </p>
                <p data-testid="motion-card-activity" className="text-lg font-semibold">
                  {t(`types.${activity.type}`)} · {activity.duration_minutes}{" "}
                  {t("common.min")}
                </p>
              </div>
            </div>

            {/* Main message */}
            <h2
              data-testid="motion-card-summary"
              className="mt-6 text-2xl font-semibold tracking-tight leading-snug"
            >
              {t("motionCard.summary", {
                duration: minutesLong(activity.duration_minutes),
              })}
            </h2>

            <div className="mt-5 space-y-2 text-sm">
              <p
                data-testid="motion-card-goal"
                className="text-[hsl(var(--muted-foreground))]"
              >
                {t("motionCard.goalLabel")}:{" "}
                <span className="text-white/90 font-medium">
                  {t(`goals.${profile.goal}`)}
                </span>
              </p>
              <p
                data-testid="motion-card-status"
                className="text-[hsl(var(--muted-foreground))]"
              >
                {t("motionCard.statusLabel")}:{" "}
                <span className="text-cyan-300 font-medium">{status}</span>
              </p>
            </div>

            <div className="mt-6 pt-5 border-t border-white/10">
              <p
                data-testid="motion-card-sentence"
                className="motion-message text-sm leading-relaxed font-medium"
              >
                {sentence}
              </p>
            </div>

            {/* Meta row */}
            <div className="mt-5 flex flex-wrap gap-2 text-[11px] text-[hsl(var(--muted-foreground))]">
              <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1">
                {t("motionCard.intensityLabel")}: {t(`intensities.${activity.intensity}`)}
              </span>
              {activity.mood_before && activity.mood_after && (
                <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 flex items-center gap-1">
                  {t(`moods.${activity.mood_before}`)} <MoveRight size={11} />{" "}
                  {t(`moods.${activity.mood_after}`)}
                </span>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 space-y-3"
        >
          <Button
            data-testid="motion-card-done-button"
            onClick={() => navigate("/", { replace: true })}
            className="w-full py-6 rounded-xl bg-cyan-300 text-slate-950 text-base font-semibold hover:bg-cyan-200 active:scale-[0.98] shadow-[0_0_0_1px_rgba(34,211,238,0.25),0_12px_40px_rgba(34,211,238,0.18)]"
          >
            <LayoutDashboard size={18} className="mr-1" /> {t("motionCard.done")}
          </Button>
          <Button
            data-testid="motion-card-progress-button"
            onClick={() => navigate("/progress", { replace: true })}
            variant="outline"
            className="w-full py-6 rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white active:scale-[0.98]"
          >
            {t("motionCard.viewProgress")}
          </Button>
        </motion.div>
      </div>

      {/* Stage 2.2: once-only challenge completion celebration */}
      <ChallengeCelebration celebration={celebration} onDismiss={dismiss} />
    </div>
  );
}
